import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { axiosClient } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import ScienceStudiesContent from '../components/ScienceStudiesContent';
import SEO from '../components/SEO';

interface ContentPageData {
  slug: string;
  titles: { [key: string]: string };
  contents: { [key: string]: string };
}

type LangKey = 'en' | 'et' | 'fi' | 'ru';

const ContentPage: React.FC = () => {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const { t } = useI18n();
  const [pageData, setPageData] = useState<ContentPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Map URL language codes to content keys
  const getContentLang = (urlLang: string | undefined): LangKey => {
    if (urlLang === 'ee') return 'et'; // Map Estonian URL code to content key
    return (urlLang || 'en') as LangKey;
  };

  const currentLang = getContentLang(lang);

  const pageTitle = pageData
    ? (pageData.titles[currentLang] || pageData.titles.en)
    : t.common.loading;
  
  useDocumentTitle(pageTitle);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) return;
      setLoading(true);
      setError(false);
      try {
        const response = await axiosClient.get(`/api/content-pages/${slug}`);
        const rawData = response.data;

        // Parse JSON strings from the database
        const parsedData = {
          ...rawData,
          titles: typeof rawData.titles === 'string' ? JSON.parse(rawData.titles) : rawData.titles,
          contents: typeof rawData.contents === 'string' ? JSON.parse(rawData.contents) : rawData.contents,
        };

        setPageData(parsedData);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return null;
  }

  if (error || !pageData) {
    return <div className="container mx-auto p-4">{t.errors.notFound}</div>;
  }

  const title = pageData.titles[currentLang] || pageData.titles.en;
  const content = pageData.contents[currentLang] || pageData.contents.en;

  // Use specialized component for science studies page
  const isScientStudiesPage = slug === 'science-studies';

  // Get SEO description based on slug
  const getSeoDescription = (slug: string | undefined) => {
    switch(slug) {
      case 'privacy': return t.seo.privacyDescription;
      case 'terms': return t.seo.termsDescription;
      case 'science-studies': return t.seo.scienceStudiesDescription;
      default: return t.seo.defaultDescription;
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <SEO description={getSeoDescription(slug)} />
      <h1 className="text-3xl font-bold mb-6 text-center">{title}</h1>
      {isScientStudiesPage ? (
        <ScienceStudiesContent content={content} />
      ) : (
        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
    </div>
  );
};

export default ContentPage;
